import React, { useState } from 'react';
import { Download, Copy, Check, Monitor, Smartphone, Image as ImageIcon, FileText, Sparkles, Music, Headphones } from 'lucide-react';
import { toast } from 'sonner';
import { NoIndex } from '../components/SEO/NoIndex';

const COVER_ART = [
    { file: 'cover-walkthrough.jpg', title: 'Walk-through cover', use: 'YouTube thumbnail, blog header, podcast art' },
    { file: 'cover-tiktok-15s.jpg', title: 'TikTok / Reels cover', use: 'Ad creative, App Store feature graphic' },
    { file: 'cover-story-30s.jpg', title: 'Stories / Shorts cover', use: 'Instagram Story sticker, LinkedIn cover' },
];

const MUSIC_SAMPLES = [];

// The actual track applied to all three promo videos
const ACTIVE_MUSIC = {
    file: 'the-mountain.mp3',
    title: 'The Mountain',
    subtitle: 'Mental Health · 2:06 · Stereo · 192kbps MP3',
    note: 'This is the licensed track currently mixed into all three promo videos. The 84s, 30s, and 15s edits each use a fade-out matched to their length.',
};

// Asset metadata — matches files in /public/promo/
const DESKTOP_ASSETS = [
    { num: '01', file: '01_dashboard_welcome_maya.jpeg', title: 'Dashboard — "Welcome back, Maya"', use: 'Hero shot for ads, App Store, web landing pages' },
    { num: '02', file: '02_sage_ai_guide.jpeg', title: 'Sage AI Guide — Intro', use: 'Highlight the AI guide feature' },
    { num: '11', file: '11_sage_conversation_overwhelmed.jpeg', title: 'Sage in Conversation', use: 'Show empathetic AI in action' },
    { num: '03', file: '03_breathing_orb.jpeg', title: 'Calm Cycle / Breathing Orb', use: 'Calming visual for grounding tools' },
    { num: '04', file: '04_regulate_hub.jpeg', title: 'Regulate Hub', use: '"Your regulation toolkit" overview' },
    { num: '05', file: '05_meditations.jpeg', title: 'Guided Meditations Library', use: 'Show breadth of meditation content' },
    { num: '06', file: '06_relationship_hub.jpeg', title: 'Relationship Hub', use: 'Relationship feature card' },
    { num: '07', file: '07_infinity_loop.jpeg', title: 'Infinity Loop (EFT)', use: 'Couples / relationships pillar — unique IP shot' },
    { num: '08', file: '08_tools_library.jpeg', title: 'Tools Library', use: 'Value density — show "we have a lot"' },
    { num: '09', file: '09_beta_signup.jpeg', title: 'Beta Signup CTA', use: '"30 days free" campaign hero' },
    { num: '10', file: '10_saved_favourites.jpeg', title: 'Saved Favourites', use: 'Personalisation / continuity message' },
];

const MOBILE_ASSETS = [
    { num: '12', file: '12_mobile_dashboard.jpeg', title: 'Mobile Dashboard', use: 'App Store screenshot #1 — hero' },
    { num: '13', file: '13_mobile_sage.jpeg', title: 'Mobile Sage', use: 'App Store screenshot — AI guide' },
    { num: '14', file: '14_mobile_regulate.jpeg', title: 'Mobile Regulate Toolkit', use: 'TikTok / Reels b-roll' },
    { num: '15', file: '15_mobile_breathing_orb.jpeg', title: 'Mobile Calm Cycle', use: 'TikTok / Reels b-roll — calming' },
    { num: '16', file: '16_mobile_infinity_loop.jpeg', title: 'Mobile Infinity Loop', use: 'Couples-targeted ad creative' },
    { num: '17', file: '17_mobile_beta.jpeg', title: 'Mobile Beta CTA', use: 'Mobile ad CTA frame' },
];

const RECORDING_SCRIPT = [
    { time: '0–5s', action: 'Open /home — show "Welcome back, Maya" + check-in card' },
    { time: '5–15s', action: 'Click into Sage — type "I\'m feeling overwhelmed" — let Sage respond' },
    { time: '15–25s', action: 'Sage auto-navigates to Calm Cycle — show breathing orb in motion' },
    { time: '25–35s', action: 'Back to Regulate — open Infinity Loop — show the EFT diagram' },
    { time: '35–45s', action: 'Open /regulate/guided-meditations — hover RSD meditation' },
    { time: '45–60s', action: 'End on /beta — "30 days free. No credit card."' },
];

const SOCIAL_COPY = {
    'Twitter / X': `🧠 We built an emotional regulation app for ADHD minds.

Not another "just breathe" app.

We get:
• RSD (when rejection hits like a truck)
• Emotional flooding (0-100 in seconds)
• Time blindness during meltdowns
• How ADHD affects relationships

30 days free. Help us make it perfect.

🔗 [link]`,
    'Instagram Caption': `Why "just breathe" doesn't work for ADHD 🧠

Your nervous system needs more than generic meditation:
✓ RSD makes rejection feel like death
✓ Emotional flooding hits before you can think
✓ Time blindness makes "wait it out" impossible

We built something different — meet CoRegulate.
30 days free. Link in bio.`,
    'Reddit (r/ADHD)': `Title: We built an emotional regulation app specifically for ADHD - looking for beta testers (30 days free)

Hey r/ADHD,

I'm one of the creators of CoRegulate. We built it because every regulation app we tried was clearly designed for neurotypical brains.

What makes it different:
- Tools that work in 60 seconds (no 20-minute meditations mid-meltdown)
- AI guide that actually understands RSD, emotional flooding, time blindness
- Relationship tools designed for how ADHD affects partnerships
- Meditations specifically for RSD and ADHD anxiety

Looking for honest beta feedback. 30 days of premium, no credit card.

[link]`,
};

const HASHTAGS = '#ADHD #ADHDLife #ADHDAwareness #EmotionalRegulation #MentalHealth #Neurodivergent #RSD #ADHDRelationships #ADHDWomen #ADHDAdult #BetaTesting #ADHDBrain';

function CopyBlock({ label, text }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success(`${label} copied`);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <div className="glass-card p-5 rounded-2xl" data-testid={`copy-block-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-[#344648]">{label}</h4>
                <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#344648] text-white text-xs font-medium hover:bg-[#2a3a3c] transition-colors"
                    data-testid={`copy-btn-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-[#344648]/80 font-sans leading-relaxed">{text}</pre>
        </div>
    );
}

function AssetCard({ asset, type }) {
    const url = `/promo/${asset.file}`;
    return (
        <div className="glass-card rounded-2xl overflow-hidden flex flex-col" data-testid={`asset-card-${asset.num}`}>
            <div className={`relative bg-[#1a1a1a]/5 ${type === 'mobile' ? 'aspect-[390/844] max-h-96' : 'aspect-[16/9]'} overflow-hidden flex items-center justify-center`}>
                <img
                    src={url}
                    alt={asset.title}
                    loading="lazy"
                    className="w-full h-full object-contain"
                />
                <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white text-xs font-medium">
                    #{asset.num}
                </span>
            </div>
            <div className="p-4 flex-1 flex flex-col gap-2">
                <h3 className="font-semibold text-[#344648] text-sm leading-snug">{asset.title}</h3>
                <p className="text-xs text-[#344648]/70 flex-1">{asset.use}</p>
                <a
                    href={url}
                    download={asset.file}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-[#344648] text-white text-xs font-medium hover:bg-[#2a3a3c] transition-colors mt-2"
                    data-testid={`download-btn-${asset.num}`}
                >
                    <Download className="w-3.5 h-3.5" />
                    Download
                </a>
            </div>
        </div>
    );
}

export default function PromoAssets() {
    const downloadAll = () => {
        // Trigger sequential downloads of screenshots + cover art
        [...DESKTOP_ASSETS, ...MOBILE_ASSETS, ...COVER_ART.map(c => ({ file: c.file }))].forEach((asset, i) => {
            setTimeout(() => {
                const link = document.createElement('a');
                link.href = `/promo/${asset.file}`;
                link.download = asset.file;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }, i * 300);
        });
        toast.success('Downloading all 20 assets...');
    };

    return (
        <div className="min-h-screen py-12 px-4 md:px-8" style={{ background: 'radial-gradient(ellipse at 50% 0%, #FDEFDC 0%, #FBF6EE 35%, #FBF6EE 100%)' }} data-testid="promo-assets-page">
            <NoIndex />
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <header className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/60 backdrop-blur border border-white/80 mb-6">
                        <Sparkles className="w-4 h-4 text-[#FFBB98]" />
                        <span className="text-sm font-medium text-[#344648]">Pre-launch press kit</span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-[#344648] mb-4 tracking-tight">
                        CoRegulate <span className="text-[#FFBB98]">Promo Materials</span>
                    </h1>
                    <p className="text-base md:text-lg text-[#344648]/75 max-w-2xl mx-auto">
                        Public asset gallery — no login required. Download screenshots, copy social captions, and grab the recording script for the launch video.
                    </p>
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                        <button
                            onClick={downloadAll}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#344648] text-white font-medium hover:bg-[#2a3a3c] transition-colors"
                            data-testid="download-all-btn"
                        >
                            <Download className="w-4 h-4" /> Download all 20 assets
                        </button>
                        <a
                            href="/docs/PROMO_MATERIALS.md"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/70 backdrop-blur border border-white/80 text-[#344648] font-medium hover:bg-white transition-colors"
                            data-testid="view-doc-btn"
                        >
                            <FileText className="w-4 h-4" /> Full marketing doc
                        </a>
                    </div>
                </header>

                {/* Featured video */}
                <section className="mb-14">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-2 h-2 rounded-full bg-[#FFBB98] animate-pulse" />
                        <span className="text-sm font-medium text-[#344648]/80 uppercase tracking-wider">Featured · 84-second walk-through</span>
                    </div>
                    <div className="grid lg:grid-cols-3 gap-6">
                        {/* Main 16:9 video */}
                        <div className="lg:col-span-2 glass-card rounded-3xl overflow-hidden border border-white/80 shadow-xl">
                            <video
                                controls
                                playsInline
                                preload="metadata"
                                poster="/promo/coregulate-demo-poster.jpg"
                                className="w-full aspect-video bg-black"
                                data-testid="promo-video-player"
                            >
                                <source src="/promo/coregulate-demo.mp4" type="video/mp4" />
                                <source src="/promo/coregulate-demo.webm" type="video/webm" />
                                Your browser doesn't support HTML5 video.
                            </video>
                            <div className="p-5 md:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <h3 className="font-serif text-xl text-[#344648]">Maya's day with CoRegulate</h3>
                                    <p className="text-sm text-[#344648]/70 mt-1">
                                        Real session — dashboard → Sage → Calm Cycle → Infinity Loop → Meditations → Beta CTA. With original instrumental score.
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2 flex-shrink-0">
                                    <a
                                        href="/promo/coregulate-demo.mp4"
                                        download="coregulate-demo.mp4"
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#344648] text-white text-sm font-medium hover:bg-[#2a3a3c] transition-colors"
                                        data-testid="download-mp4-btn"
                                    >
                                        <Download className="w-4 h-4" /> MP4
                                    </a>
                                    <a
                                        href="/promo/coregulate-demo.webm"
                                        download="coregulate-demo.webm"
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-white/80 text-[#344648] text-sm font-medium hover:bg-white transition-colors"
                                        data-testid="download-webm-btn"
                                    >
                                        <Download className="w-4 h-4" /> WebM
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Vertical column: 15s + 30s */}
                        <div className="flex flex-col gap-6">
                            {/* 15s vertical */}
                            <div className="glass-card rounded-3xl overflow-hidden border border-white/80 shadow-xl flex flex-col">
                                <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFBB98]/20 text-[#344648] text-xs font-semibold">
                                        <Smartphone className="w-3.5 h-3.5" /> 15s · TikTok / Reels
                                    </span>
                                </div>
                                <div className="px-5 flex-1 flex items-center justify-center bg-transparent">
                                    <video
                                        controls
                                        playsInline
                                        preload="metadata"
                                        poster="/promo/coregulate-demo-vertical-poster.jpg"
                                        className="h-[480px] w-auto aspect-[9/16] rounded-2xl"
                                        data-testid="promo-video-vertical"
                                    >
                                        <source src="/promo/coregulate-demo-vertical-15s.mp4" type="video/mp4" />
                                    </video>
                                </div>
                                <div className="p-5 flex flex-col gap-3">
                                    <p className="text-xs text-[#344648]/70 leading-relaxed">
                                        Quick highlight reel with brand frame. Best for ads, TikTok hooks, Reels.
                                    </p>
                                    <a
                                        href="/promo/coregulate-demo-vertical-15s.mp4"
                                        download="coregulate-demo-vertical-15s.mp4"
                                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#344648] text-white text-sm font-medium hover:bg-[#2a3a3c] transition-colors"
                                        data-testid="download-vertical-btn"
                                    >
                                        <Download className="w-4 h-4" /> Download 15s
                                    </a>
                                </div>
                            </div>

                            {/* 30s story */}
                            <div className="glass-card rounded-3xl overflow-hidden border border-white/80 shadow-xl flex flex-col">
                                <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#344648]/10 text-[#344648] text-xs font-semibold">
                                        <Smartphone className="w-3.5 h-3.5" /> 30s · Stories / Shorts · captions
                                    </span>
                                </div>
                                <div className="px-5 flex-1 flex items-center justify-center bg-transparent">
                                    <video
                                        controls
                                        playsInline
                                        preload="metadata"
                                        poster="/promo/coregulate-demo-story-poster.jpg"
                                        className="h-[480px] w-auto aspect-[9/16] rounded-2xl"
                                        data-testid="promo-video-story"
                                    >
                                        <source src="/promo/coregulate-demo-story-30s.mp4" type="video/mp4" />
                                    </video>
                                </div>
                                <div className="p-5 flex flex-col gap-3">
                                    <p className="text-xs text-[#344648]/70 leading-relaxed">
                                        Captioned for sound-off viewing. Ideal for Instagram Stories, YouTube Shorts, LinkedIn video.
                                    </p>
                                    <a
                                        href="/promo/coregulate-demo-story-30s.mp4"
                                        download="coregulate-demo-story-30s.mp4"
                                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#344648] text-white text-sm font-medium hover:bg-[#2a3a3c] transition-colors"
                                        data-testid="download-story-btn"
                                    >
                                        <Download className="w-4 h-4" /> Download 30s
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Music — track in use */}
                <section className="mb-14" data-testid="music-samples-section">
                    <div className="flex items-center gap-3 mb-6">
                        <Headphones className="w-6 h-6 text-[#344648]" />
                        <h2 className="text-2xl md:text-3xl font-serif text-[#344648]">Soundtrack <span className="text-[#344648]/50 font-sans text-base">· now applied to all videos</span></h2>
                    </div>
                    <div className="glass-card rounded-2xl p-6 border border-white/80" data-testid="active-music">
                        <div className="flex items-start gap-4 mb-4">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFBB98] to-[#f5a87f] flex items-center justify-center flex-shrink-0">
                                <Music className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-serif text-2xl text-[#344648]">{ACTIVE_MUSIC.title}</h3>
                                <p className="text-sm text-[#344648]/60 mt-1">{ACTIVE_MUSIC.subtitle}</p>
                            </div>
                            <a
                                href={`/promo/${ACTIVE_MUSIC.file}`}
                                download={`coregulate-${ACTIVE_MUSIC.file}`}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#344648] text-white text-sm font-medium hover:bg-[#2a3a3c] transition-colors flex-shrink-0"
                                data-testid="active-music-download"
                            >
                                <Download className="w-4 h-4" /> Download
                            </a>
                        </div>
                        <audio controls preload="metadata" className="w-full" data-testid="active-music-audio">
                            <source src={`/promo/${ACTIVE_MUSIC.file}`} type="audio/mpeg" />
                        </audio>
                        <p className="text-sm text-[#344648]/75 mt-4 leading-relaxed">{ACTIVE_MUSIC.note}</p>
                    </div>
                </section>

                {/* Cover art */}
                <section className="mb-14">
                    <div className="flex items-center gap-3 mb-6">
                        <Music className="w-6 h-6 text-[#344648]" />
                        <h2 className="text-2xl md:text-3xl font-serif text-[#344648]">Cover art <span className="text-[#344648]/50 font-sans text-base">· 1080 × 1080 · 3 assets</span></h2>
                    </div>
                    <p className="text-sm text-[#344648]/70 mb-6 max-w-2xl">
                        Square cover graphics paired with each video — perfect for YouTube thumbnails, App Store feature graphics, podcast art, Instagram Story stickers, and ad creatives.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {COVER_ART.map((c, i) => (
                            <div key={c.file} className="glass-card rounded-2xl overflow-hidden flex flex-col" data-testid={`cover-card-${i}`}>
                                <div className="aspect-square bg-[#1a1a1a]/5 overflow-hidden">
                                    <img src={`/promo/${c.file}`} alt={c.title} loading="lazy" className="w-full h-full object-cover" />
                                </div>
                                <div className="p-4 flex-1 flex flex-col gap-2">
                                    <h3 className="font-semibold text-[#344648] text-sm">{c.title}</h3>
                                    <p className="text-xs text-[#344648]/70 flex-1">{c.use}</p>
                                    <a
                                        href={`/promo/${c.file}`}
                                        download={c.file}
                                        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-[#344648] text-white text-xs font-medium hover:bg-[#2a3a3c] transition-colors mt-2"
                                        data-testid={`cover-download-${i}`}
                                    >
                                        <Download className="w-3.5 h-3.5" />
                                        Download
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Demo persona */}
                <section className="mb-14">
                    <div className="glass-card rounded-3xl p-6 md:p-8 border border-white/80">
                        <div className="flex items-start gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFBB98] to-[#f5a87f] flex items-center justify-center flex-shrink-0">
                                <span className="text-2xl">🌅</span>
                            </div>
                            <div className="flex-1">
                                <h2 className="text-2xl font-serif text-[#344648] mb-1">Demo persona — "Maya"</h2>
                                <p className="text-sm text-[#344648]/70 mb-4">
                                    Use this account for screen recordings & live demos so the dashboard greets <strong>"Welcome back, Maya"</strong> — never "Test User".
                                </p>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    <CopyBlock label="Email" text="maya@coregulateadhd.com" />
                                    <CopyBlock label="Password" text="DemoPromo2026!" />
                                </div>
                                <p className="text-xs text-[#344648]/60 mt-3">
                                    Tier: <code className="px-1.5 py-0.5 rounded bg-white/60 text-[#344648]">support_plus</code> (premium unlocked, no paywalls visible during recording).
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Desktop assets */}
                <section className="mb-14">
                    <div className="flex items-center gap-3 mb-6">
                        <Monitor className="w-6 h-6 text-[#344648]" />
                        <h2 className="text-2xl md:text-3xl font-serif text-[#344648]">Desktop screenshots <span className="text-[#344648]/50 font-sans text-base">· 1920 × 1080 · 11 assets</span></h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {DESKTOP_ASSETS.map(a => <AssetCard key={a.num} asset={a} type="desktop" />)}
                    </div>
                </section>

                {/* Mobile assets */}
                <section className="mb-14">
                    <div className="flex items-center gap-3 mb-6">
                        <Smartphone className="w-6 h-6 text-[#344648]" />
                        <h2 className="text-2xl md:text-3xl font-serif text-[#344648]">Mobile screenshots <span className="text-[#344648]/50 font-sans text-base">· 390 × 844 · 6 assets</span></h2>
                    </div>
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                        {MOBILE_ASSETS.map(a => <AssetCard key={a.num} asset={a} type="mobile" />)}
                    </div>
                </section>

                {/* Recording script */}
                <section className="mb-14">
                    <div className="flex items-center gap-3 mb-6">
                        <ImageIcon className="w-6 h-6 text-[#344648]" />
                        <h2 className="text-2xl md:text-3xl font-serif text-[#344648]">60-second video script</h2>
                    </div>
                    <div className="glass-card rounded-3xl p-6 md:p-8">
                        <p className="text-sm text-[#344648]/75 mb-6">
                            Sign in as <strong>Maya</strong> in an incognito window. Use Loom, Screen Studio, or QuickTime. Follow this flow:
                        </p>
                        <ol className="space-y-3">
                            {RECORDING_SCRIPT.map((step, i) => (
                                <li key={i} className="flex items-start gap-4 p-4 rounded-2xl bg-white/50 border border-white/60">
                                    <span className="px-3 py-1 rounded-full bg-[#344648] text-white text-xs font-bold flex-shrink-0">{step.time}</span>
                                    <span className="text-sm text-[#344648]">{step.action}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* Social copy */}
                <section className="mb-14">
                    <div className="flex items-center gap-3 mb-6">
                        <FileText className="w-6 h-6 text-[#344648]" />
                        <h2 className="text-2xl md:text-3xl font-serif text-[#344648]">Ready-to-paste social copy</h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                        {Object.entries(SOCIAL_COPY).map(([k, v]) => (
                            <CopyBlock key={k} label={k} text={v} />
                        ))}
                        <div className="md:col-span-2">
                            <CopyBlock label="Hashtags" text={HASHTAGS} />
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="text-center pt-8 border-t border-[#344648]/10">
                    <p className="text-sm text-[#344648]/60">
                        CoRegulate · Made for ADHD hearts, shaped by ADHD minds.
                    </p>
                    <p className="text-xs text-[#344648]/40 mt-2">
                        These materials are for the CoRegulate launch team. Share responsibly.
                    </p>
                </footer>
            </div>
        </div>
    );
}
